import type { TFunction } from 'i18next';

import { SupportSessionAction } from '../../common/hooks/use-support-sessions-api.hook';

/** Areas the API names (support-action.describe.ts); anything else reads as 'other'. */
const AREAS = [
    'products', 'categories', 'families', 'productTypes', 'spaces', 'settings', 'profile', 'commerce', 'contentStudio',
    'social', 'ads', 'leads', 'automations', 'emails', 'apiIntegration', 'integration', 'assistant', 'other',
];

/** Did this change go through? Refused or failed requests changed nothing. */
export const wentThrough = (a: SupportSessionAction) => !a.status || a.status < 400;

/**
 * One change, as a sentence the vendor understands: "Updated the product
 * “Oak Herringbone”", "Made changes in Settings". The details line says what
 * happened to a product's variants.
 */
export function describeAction(a: SupportSessionAction, t: TFunction): { sentence: string; details?: string } {
    const s = a.summary;
    if (!s) return { sentence: t('SUPPORT_ACCESS.CHANGE.UNKNOWN') };

    const area = AREAS.includes(s.area) ? s.area : 'other';
    const noun = t(`SUPPORT_ACCESS.CHANGE.AREA.${area}`);
    const place = t(`SUPPORT_ACCESS.CHANGE.PLACE.${area}`);
    const verb = s.verb.toUpperCase();

    let sentence: string;
    if (s.verb === 'run') {
        const action = (s.action || '').replace(/[-_]/g, ' ');
        sentence = t('SUPPORT_ACCESS.CHANGE.RUN', { action, place });
    } else if (s.name) {
        sentence = t(`SUPPORT_ACCESS.CHANGE.${verb}_NAMED`, { area: noun, name: s.name });
    } else {
        sentence = t(`SUPPORT_ACCESS.CHANGE.${verb}`, { place });
    }

    const v = s.variants;
    const parts = v
        ? [
            v.created ? t('SUPPORT_ACCESS.CHANGE.VARIANTS_ADDED', { count: v.created }) : null,
            v.updated ? t('SUPPORT_ACCESS.CHANGE.VARIANTS_UPDATED', { count: v.updated }) : null,
            v.deleted ? t('SUPPORT_ACCESS.CHANGE.VARIANTS_REMOVED', { count: v.deleted }) : null,
        ].filter(Boolean)
        : [];
    return { sentence, details: parts.length ? parts.join(' · ') : undefined };
}
